import { Stack, Typography } from '@mui/joy';
import { ArrowRight } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import {
  AttributeValueChip,
  getAttributeValueLabel,
} from 'companySettings/components/PartFinderAttributeValues';
import { CompareGroup, CompareView } from 'companySettings/components/PartFinderVersionHistory';
import { useComparePartFinderAttributeVersions } from 'companySettings/hooks';
import { IPartFinderAttribute } from 'companySettings/types';
import { Color, Direction, Level, Size } from 'theme/enums';

interface IProps {
  attribute: IPartFinderAttribute;
  fromVersionId: number;
  toVersionId: number;
  isOpen: boolean;
  onClose: () => void;
}

// What changed between two published versions of a list, older to newer.
export const PartFinderAttributeCompare = ({
  attribute,
  fromVersionId,
  toVersionId,
  isOpen,
  onClose,
}: IProps) => {
  const { t } = useTranslation();

  const {
    data: comparison,
    isLoading,
    isError,
  } = useComparePartFinderAttributeVersions(fromVersionId, toVersionId, isOpen);

  const hasValueChanges = Boolean(
    comparison?.added?.length ||
      comparison?.removed?.length ||
      comparison?.changed?.length ||
      comparison?.reordered
  );

  const renderChip = (value: IPartFinderAttribute['values'][number], key?: string) => (
    <AttributeValueChip
      key={key ?? getAttributeValueLabel(attribute.type, value, t)}
      value={value}
      type={attribute.type}
      size={Size.SM}
    />
  );

  return (
    <CompareView
      comparison={comparison}
      isLoading={isLoading}
      isError={isError}
      errorMessage={t('errorTitlePartFinderAttributeCompare')}
      isOpen={isOpen}
      onClose={onClose}
    >
      {comparison && (
        <Stack sx={{ gap: 1 }}>
          <Typography level={Level.TITLE_MD}>{t('labelValues')}</Typography>

          {comparison.added?.length > 0 && (
            <CompareGroup
              title={t('labelValuesAdded', { count: comparison.added.length })}
              color={Color.SUCCESS}
            >
              {comparison.added.map((value) => renderChip(value))}
            </CompareGroup>
          )}

          {comparison.removed?.length > 0 && (
            <CompareGroup
              title={t('labelValuesRemoved', { count: comparison.removed.length })}
              color={Color.DANGER}
            >
              {comparison.removed.map((value) => renderChip(value))}
            </CompareGroup>
          )}

          {comparison.changed?.length > 0 && (
            <CompareGroup
              title={t('labelValuesChanged', { count: comparison.changed.length })}
              color={Color.WARNING}
            >
              {comparison.changed.map(({ from, to }) => (
                <Stack
                  key={getAttributeValueLabel(attribute.type, to, t)}
                  direction={Direction.ROW}
                  sx={{ alignItems: 'center', gap: 0.5 }}
                >
                  {renderChip(from, 'from')}
                  <ArrowRight />
                  {renderChip(to, 'to')}
                </Stack>
              ))}
            </CompareGroup>
          )}

          {comparison.reordered && (
            <Typography level={Level.TITLE_SM}>{t('labelValuesReordered')}</Typography>
          )}

          {!hasValueChanges && (
            <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
              {t('labelNoChanges')}
            </Typography>
          )}
        </Stack>
      )}
    </CompareView>
  );
};
